import { reportWeekday, shiftReportDate } from './report-zone'
import type {
  OrderProposalArticle,
  OrderProposalColumnMeta,
  OrderProposalReport,
  OrderProposalSales,
} from '@/features/reports/api/use-order-proposal-query'
import {
  addStockQuantities,
  subtractStockQuantities,
} from '@/shared/lib/stock-quantity'

/**
 * Everything the Bestellvorschlag derives from the server's raw figures
 * (CHG-2026-10-bestellvorschlag, D6–D8): date defaults, Gesamt, proposals, Ø Liefertage, the
 * expected sale of a planned Tagesgericht, "Bestellung füllen", and the rows a filter leaves on
 * screen. Pure, so the table, the CSV and the fill bar cannot disagree.
 */

// ---------------------------------------------------------------------------------------------
// Dates (D6, D11)
// ---------------------------------------------------------------------------------------------

export interface DateColumn {
  from: string
  to: string
}

/**
 * How a date column is chosen. The relative modes are kept as modes, not dates, so a remembered
 * choice is still right the next day; a fixed range is kept only when the user picked it.
 */
export type ColumnMode =
  | 'liefertag-7'
  | 'yesterday'
  | 'last7'
  | 'last14'
  | { from: string; to: string }

export const COLUMN_PRESETS = [
  'liefertag-7',
  'yesterday',
  'last7',
  'last14',
] as const

export type ColumnPreset = (typeof COLUMN_PRESETS)[number]

/** Date column 1 when nothing is remembered: the order days one week earlier. */
export const DEFAULT_COLUMN_1: ColumnMode = 'liefertag-7'

/** The default Liefertag: the next Mon–Fri day after today (Vienna calendar). */
export function nextWeekday(today: string): string {
  let day = shiftReportDate(today, 1)
  while (reportWeekday(day) > 5) day = shiftReportDate(day, 1)
  return day
}

/**
 * The dates of a column mode. `delivery` are the order days (the Liefertag, or a Liefertag range);
 * `liefertag-7` is them one week earlier, a day or a range alike.
 */
export function resolveColumn(
  mode: ColumnMode,
  today: string,
  delivery: DateColumn,
): DateColumn {
  switch (mode) {
    case 'liefertag-7':
      return {
        from: shiftReportDate(delivery.from, -7),
        to: shiftReportDate(delivery.to, -7),
      }
    case 'yesterday': {
      const day = shiftReportDate(today, -1)
      return { from: day, to: day }
    }
    case 'last7':
      return {
        from: shiftReportDate(today, -7),
        to: shiftReportDate(today, -1),
      }
    case 'last14':
      return {
        from: shiftReportDate(today, -14),
        to: shiftReportDate(today, -1),
      }
    default:
      return mode.from <= mode.to
        ? { from: mode.from, to: mode.to }
        : { from: mode.to, to: mode.from }
  }
}

export function isSingleDay(column: DateColumn): boolean {
  return column.from === column.to
}

// ---------------------------------------------------------------------------------------------
// Figures (D5, D7, D8)
// ---------------------------------------------------------------------------------------------

/** Gesamt = Inventur (negative counts as 0) + Zukauf. Missing values count as 0. */
export function gesamt(onHand: number | null, incoming: number | null): number {
  return addStockQuantities(Math.max(onHand ?? 0, 0), incoming ?? 0)
}

/** "Sell the same again": the sale minus what is there, never below 0. */
export function proposal(sale: number, total: number): number {
  return Math.max(0, subtractStockQuantities(sale, total))
}

type ColumnDays = Pick<OrderProposalColumnMeta, 'days' | 'weekdayCounts'>

/**
 * The column's average sale on the weekday of `day`, days without sales included; a weekday that
 * does not occur in the column uses the column's average per day (D7). Unrounded.
 */
function weekdayAverage(
  sales: OrderProposalSales,
  column: ColumnDays,
  day: string,
): number {
  const weekday = reportWeekday(day) - 1
  const count = column.weekdayCounts[weekday] ?? 0
  if (count > 0) return (sales.byWeekday[weekday] ?? 0) / count
  return column.days > 0 ? sales.total / column.days : 0
}

/**
 * Ø Liefertage (D7): for every order day the column's average of the same weekday, added up —
 * never one average times the number of days — and rounded once to a whole unit before it enters
 * a proposal.
 */
export function orderDaysAverage(
  sales: OrderProposalSales,
  column: ColumnDays,
  orderDays: Array<string>,
): number {
  return Math.round(
    orderDays.reduce((sum, day) => sum + weekdayAverage(sales, column, day), 0),
  )
}

/**
 * What one planned Tagesgericht is expected to sell (D4): for every order day it is planned on,
 * the column's Tagesgerichte average of that weekday shared by the dishes planned that day, added
 * up and rounded once to whole dishes.
 */
export function expectedDishSale(
  totals: OrderProposalSales,
  column: ColumnDays,
  dishDays: Array<string>,
  dishesOn: (day: string) => number,
): number {
  return Math.round(
    dishDays.reduce((sum, day) => {
      const dishes = dishesOn(day)
      return dishes > 0
        ? sum + weekdayAverage(totals, column, day) / dishes
        : sum
    }, 0),
  )
}

/**
 * "Bestellung füllen" (D8): the expected sale plus the uplift, rounded up, minus Gesamt, never
 * below 0. The uplift applies to the sale, not to the proposal, so an article whose proposal is 0
 * can still get a buffer.
 */
export function fillQuantity(
  sale: number,
  total: number,
  upliftPercent: number,
): number {
  // The epsilon keeps 20 × 1,2 = 24.000000000000004 from rounding up to 25.
  const demand = Math.ceil(sale * (1 + upliftPercent / 100) - 1e-9)
  return Math.max(0, subtractStockQuantities(demand, total))
}

/**
 * The Bestellung a clicked Vorschlag cell takes over: exactly the number the cell shows, without
 * the fill bar's uplift; 0 stays empty, as in {@link fillOrders}.
 */
export function takenQuantity(value: number): string {
  const rounded = Math.round(value * 1000) / 1000
  return rounded > 0 ? String(rounded) : ''
}

/** Which Vorschlag cell of a row was taken over: `<column index>:sum` or `<column index>:avg`. */
export type TakenCell = `${number}:${'sum' | 'avg'}`

// ---------------------------------------------------------------------------------------------
// Rows (D3, D4, D8, D9)
// ---------------------------------------------------------------------------------------------

/** One date column's figures on a row; `average` and `proposalAvg` only for a range. */
export interface ColumnFigures {
  sale: number
  proposal: number
  average: number | null
  proposalAvg: number | null
}

export interface ProposalRow {
  /** `a:<sku>` for an article, `tg:<sku>` for a planned Tagesgericht (D9). */
  key: string
  sku: string
  name: string
  onHand: number | null
  incoming: number | null
  gesamt: number
  /** A planned Tagesgericht's order days, only when it is planned on more than one. */
  orderDays: Array<string> | null
  columns: Array<ColumnFigures>
}

export interface SoldDishRow {
  key: string
  sku: string
  name: string
  today: boolean
  /** Its Tagesgericht days other than today, newest first. */
  previousDays: Array<string>
  onHand: number | null
  incoming: number | null
  gesamt: number
  /** Sales per column; null when the dish was no Tagesgericht in that column. */
  sales: Array<number | null>
}

export interface SoldTotal {
  columns: Array<{ sale: number; average: number | null }>
}

export interface CategoryGroup {
  key: string
  name: string | null
  rows: Array<ProposalRow>
}

interface SortedGroup extends CategoryGroup {
  mainSort: number
  mainName: string
  sort: number
}

export interface ProposalView {
  /** The switch is on and the Hauptgruppe holds the Tagesgerichte. */
  showTagesgerichte: boolean
  planned: Array<ProposalRow>
  soldTotal: SoldTotal
  sold: Array<SoldDishRow>
  groups: Array<CategoryGroup>
}

export interface ProposalFilters {
  /** Hauptgruppe; null for all. */
  mainCategoryId: number | null
  showTagesgerichte: boolean
  /** "Nullzeilen ausblenden" (CHG-2026-10-bestellvorschlag-simplify, D5). */
  hideZeroRows: boolean
  /** The Bestellung typed so far: a zero row with a Bestellung stays on screen. */
  orders: Record<string, string>
  sort: ProposalSort
}

// ---------------------------------------------------------------------------------------------
// Sorting and zero rows (CHG-2026-10-bestellvorschlag-simplify, D4, D5)
// ---------------------------------------------------------------------------------------------

export type SortKey =
  | 'name'
  | 'sku'
  | 'onHand'
  | 'incoming'
  | 'gesamt'
  | 'sale'
  | 'average'
  | 'proposal'
  | 'proposalAvg'

export interface ProposalSort {
  key: SortKey
  dir: 'asc' | 'desc'
}

export const DEFAULT_SORT: ProposalSort = { key: 'name', dir: 'asc' }

const TEXT_KEYS: ReadonlyArray<SortKey> = ['name', 'sku']
const RANGE_KEYS: ReadonlyArray<SortKey> = ['average', 'proposalAvg']

/** A first click sorts text A–Z and numbers largest first; a second click reverses. */
export function nextSort(current: ProposalSort, key: SortKey): ProposalSort {
  if (current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
  }
  return { key, dir: TEXT_KEYS.includes(key) ? 'asc' : 'desc' }
}

/** The sort in effect: a range-only column (Ø) falls back to A–Z on a single day. */
export function effectiveSort(
  sort: ProposalSort,
  column: DateColumn,
): ProposalSort {
  return isSingleDay(column) && RANGE_KEYS.includes(sort.key)
    ? DEFAULT_SORT
    : sort
}

function sortValue(row: ProposalRow, key: SortKey): string | number | null {
  const first = row.columns[0]
  switch (key) {
    case 'name':
      return row.name
    case 'sku':
      return row.sku
    case 'onHand':
      return row.onHand
    case 'incoming':
      return row.incoming
    case 'gesamt':
      // Shown as "–" without any stock figure.
      return row.onHand == null && row.incoming == null ? null : row.gesamt
    case 'sale':
      return first.sale
    case 'average':
      return first.average
    case 'proposal':
      return first.proposal
    case 'proposalAvg':
      return first.proposalAvg
  }
}

/**
 * Rows in the chosen order: German collation for text, missing values ("–") last in either
 * direction, ties by Artikel A–Z.
 */
export function sortRows(
  rows: Array<ProposalRow>,
  sort: ProposalSort,
): Array<ProposalRow> {
  const sign = sort.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = sortValue(a, sort.key)
    const vb = sortValue(b, sort.key)
    if (va == null || vb == null) {
      if (va !== vb) return va == null ? 1 : -1
      return byName(a, b)
    }
    const order =
      typeof va === 'string' && typeof vb === 'string'
        ? va.localeCompare(vb, 'de')
        : Number(va) - Number(vb)
    return order === 0 ? byName(a, b) : sign * order
  })
}

function hasOrder(orders: Record<string, string>, key: string): boolean {
  return Number((orders[key] ?? '').replace(',', '.')) > 0
}

/** Inventur, Zukauf and Verkauf all 0 (missing counts as 0) and nothing ordered. */
export function isZeroRow(
  row: ProposalRow,
  orders: Record<string, string>,
): boolean {
  return (
    (row.onHand ?? 0) === 0 &&
    (row.incoming ?? 0) === 0 &&
    row.columns.every((column) => column.sale === 0) &&
    !hasOrder(orders, row.key)
  )
}

function figures(
  sale: number,
  average: number | null,
  total: number,
): ColumnFigures {
  return {
    sale,
    proposal: proposal(sale, total),
    average,
    proposalAvg: average == null ? null : proposal(average, total),
  }
}

const NO_SALES: OrderProposalSales = { total: 0, byWeekday: [] }

function articleRow(
  article: OrderProposalArticle,
  columns: Array<OrderProposalColumnMeta>,
  orderDays: Array<string>,
): ProposalRow {
  const total = gesamt(article.onHand, article.incoming)
  return {
    key: `a:${article.sku}`,
    sku: article.sku,
    name: article.name,
    onHand: article.onHand,
    incoming: article.incoming,
    gesamt: total,
    orderDays: null,
    columns: columns.map((column, i) => {
      const sales = article.sales[i] ?? NO_SALES
      return figures(
        sales.total,
        isSingleDay(column) ? null : orderDaysAverage(sales, column, orderDays),
        total,
      )
    }),
  }
}

function byName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name, 'de')
}

/** The rows on screen for a report and its filters — the table, the CSV and the fill read this. */
export function buildProposalView(
  report: OrderProposalReport,
  filters: ProposalFilters,
): ProposalView {
  const { columns, tagesgerichte } = report
  const inGroup = (mainCategoryId: number | null) =>
    filters.mainCategoryId == null || mainCategoryId === filters.mainCategoryId

  const orderDays = tagesgerichte.days.map((day) => day.date)
  const dishesOn = (date: string) =>
    tagesgerichte.days.find((day) => day.date === date)?.dishes ?? 0
  const planned: Array<ProposalRow> = tagesgerichte.planned.map((dish) => {
    const total = gesamt(dish.onHand, dish.incoming)
    return {
      key: `tg:${dish.sku}`,
      sku: dish.sku,
      name: dish.name,
      onHand: dish.onHand,
      incoming: dish.incoming,
      gesamt: total,
      orderDays: dish.days.length > 1 ? dish.days : null,
      columns: columns.map((column, i) => {
        const expected = expectedDishSale(
          tagesgerichte.totals[i] ?? NO_SALES,
          column,
          dish.days,
          dishesOn,
        )
        // A dish has no range total of its own (dishes rotate), so a range column's Σ shows the
        // same expected sale as its Ø; Vorschlag Σ and Vorschlag Ø are then equal too.
        return figures(expected, isSingleDay(column) ? null : expected, total)
      }),
    }
  })

  const soldTotal: SoldTotal = {
    columns: columns.map((column, i) => {
      const sales = tagesgerichte.totals[i] ?? NO_SALES
      return {
        sale: sales.total,
        average: isSingleDay(column)
          ? null
          : orderDaysAverage(sales, column, orderDays),
      }
    }),
  }

  const sold: Array<SoldDishRow> = tagesgerichte.sold
    .map((dish) => {
      const previousDays = dish.days
        .filter((day) => day !== report.today)
        .sort()
        .reverse()
      return {
        key: `tgs:${dish.sku}`,
        sku: dish.sku,
        name: dish.name,
        today: dish.today,
        previousDays,
        onHand: dish.onHand,
        incoming: dish.incoming,
        gesamt: gesamt(dish.onHand, dish.incoming),
        sales: columns.map((column, i) =>
          dish.days.some((day) => day >= column.from && day <= column.to)
            ? (dish.sales[i]?.total ?? 0)
            : null,
        ),
      }
    })
    .sort((a, b) => {
      if (a.today !== b.today) return a.today ? -1 : 1
      const lastA = a.previousDays[0] ?? ''
      const lastB = b.previousDays[0] ?? ''
      if (lastA !== lastB) return lastA < lastB ? 1 : -1
      return byName(a, b)
    })

  const byCategory = new Map<string, SortedGroup>()
  for (const article of report.articles) {
    if (!inGroup(article.mainCategoryId)) continue
    const key = String(article.categoryId ?? 'none')
    let group = byCategory.get(key)
    if (!group) {
      group = {
        key,
        name: article.categoryName,
        mainSort: article.mainCategorySort ?? Number.MAX_SAFE_INTEGER,
        mainName: article.mainCategoryName ?? '',
        sort: article.categorySort ?? Number.MAX_SAFE_INTEGER,
        rows: [],
      }
      byCategory.set(key, group)
    }
    group.rows.push(articleRow(article, columns, orderDays))
  }
  const groups = [...byCategory.values()]
    // Grouped by Hauptgruppe first, so with "Alle" the drinks do not land between the food groups.
    .sort(
      (a, b) =>
        a.mainSort - b.mainSort ||
        a.mainName.localeCompare(b.mainName, 'de') ||
        a.sort - b.sort ||
        (a.name ?? '').localeCompare(b.name ?? '', 'de'),
    )
    .map(({ key, name, rows }) => ({
      key,
      name,
      rows: sortRows(
        filters.hideZeroRows
          ? rows.filter((row) => !isZeroRow(row, filters.orders))
          : rows,
        filters.sort,
      ),
    }))
    // A Warengruppe whose rows are all hidden is not shown.
    .filter((group) => group.rows.length > 0)

  return {
    showTagesgerichte:
      filters.showTagesgerichte && inGroup(tagesgerichte.mainCategoryId),
    planned: sortRows(planned, filters.sort),
    soldTotal,
    sold,
    groups,
  }
}

/** The Hauptgruppen the report's rows belong to, for the filter. */
export function mainCategories(
  report: OrderProposalReport,
): Array<{ id: number; name: string }> {
  const seen = new Map<number, string>()
  for (const article of report.articles) {
    if (article.mainCategoryId != null && !seen.has(article.mainCategoryId)) {
      seen.set(article.mainCategoryId, article.mainCategoryName ?? '')
    }
  }
  return [...seen.entries()].map(([id, name]) => ({ id, name })).sort(byName)
}

/** Every row that takes a Bestellung on screen: planned Tagesgerichte (when shown), then articles. */
export function orderableRows(view: ProposalView): Array<ProposalRow> {
  return [
    ...(view.showTagesgerichte ? view.planned : []),
    ...view.groups.flatMap((group) => group.rows),
  ]
}

// ---------------------------------------------------------------------------------------------
// Bestellung füllen (D8)
// ---------------------------------------------------------------------------------------------

/** For a range: Σ fills from Verkauf, Ø from Ø Liefertage; a single day always from Verkauf. */
export type FillSource = 'sum' | 'avg'

function saleOf(row: ProposalRow, source: FillSource): number {
  const first = row.columns[0]
  return source === 'avg' ? (first.average ?? first.sale) : first.sale
}

/**
 * The Bestellung of every visible row filled from one source with an uplift; 0 stays empty. The
 * caller merges it over the typed values, so a row a filter hides keeps what was typed there.
 */
export function fillOrders(
  view: ProposalView,
  source: FillSource,
  upliftPercent: number,
): Record<string, string> {
  const orders: Record<string, string> = {}
  for (const row of orderableRows(view)) {
    const quantity = fillQuantity(
      saleOf(row, source),
      row.gesamt,
      upliftPercent,
    )
    orders[row.key] = quantity > 0 ? String(quantity) : ''
  }
  return orders
}
